import { FC, useContext, useEffect, useMemo } from "react"
import { filterToTimeFrame } from "../utility";
import { EnterpriseComplianceDashboardContext } from "../contexts/EcdContext";
import { DashboardTile } from "@app/shared/qes-standards";
import { EcdTilesEnum, TimeFrameOptions } from "../interfaces";
import EnterpriseComplianceDashboardTimeFrame from "../components/EcdTimeFrame";
import { EnterpriseComplianceFiltersContext } from "../contexts/EcdFiltersContext";
import { EcdColorsContext } from "../contexts/EcdColorsContext";
import { TooltipWrapper } from "@app/shared";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { tileFlyoutLink } from "../flyouts/EcdDrillInConfig";
import EcdExceptionsFlyout from "../flyouts/EcdExceptionsFlyout";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import ExceptionsChart from "../components/ExceptionsChart";

const ExceptionsTile: FC = () => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);
    const dashboardContext = useContext(EnterpriseComplianceDashboardContext);
    const colorsContext = useContext(EcdColorsContext);
    const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
    const title = "Exceptions/Justifications";

    const slicedExceptionsData = useMemo(() => {
        return filterToTimeFrame(dashboardContext.exceptionsData, filtersContext.exceptionsTimeFrame);
    }, [dashboardContext.exceptionsData, filtersContext.exceptionsTimeFrame]);

    useEffect(() => {
        dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.Exceptions, true);
    }, []);

    useEffect(() => {
        if (slicedExceptionsData?.length > 0) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.Exceptions, false);
        }
    }, [slicedExceptionsData]);

    useEffect(() => {
        if (!!filtersContext.apiReadyFilters) {
            dashboardContext.updateIsTileLoadingMap(EcdTilesEnum.Exceptions, true);
        }
    }, [filtersContext.apiReadyFilters]);

    const flyoutLinkTitle = useMemo(() => {
        if (!featureFlags.has(Features.ENABLE_ECD_DRILLINS) || dashboardContext.isDataEmptyMap.get(EcdTilesEnum.Exceptions)) {
            return title;
        }

        return tileFlyoutLink(
            <EcdExceptionsFlyout />,
            "Exceptions Details",
            filtersContext.isDashboardLoading || dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions),
            "exceptions-tile__title-flyout",
            title,
        );
    }, [
        filtersContext.isDashboardLoading,
        dashboardContext.exceptionsData,
        featureFlags.has(Features.ENABLE_ECD_DRILLINS),
        dashboardContext.isDataEmptyMap.get(EcdTilesEnum.Exceptions),
        dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions)
    ]);

    return (
        <DashboardTile
            headerElement={
                <EnterpriseComplianceDashboardTimeFrame
                    disabled={dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions)}
                    onChange={(value: string) => filtersContext.setExceptionsTimeFrame(value as TimeFrameOptions)}
                    selectedKey={filtersContext.exceptionsTimeFrame}
                    tileKey="exceptions-tile"
                />
            }
            loading={filtersContext.isDashboardLoading || dashboardContext.isTileLoadingMap.get(EcdTilesEnum.Exceptions)}
            title={
                <>
                    <span>{flyoutLinkTitle}</span>
                    <TooltipWrapper text="Historical Exception statuses reflect the status as of the subsequent project run date." containerClassName="ml-2">
                        <QesIcon iconName={QesIcons.FontAwesome.InfoCircle} color="#056edf" width="16" height="16" classNames="mb-1" description="informational tooltip trigger" />
                    </TooltipWrapper>
                </>
            }
            widthClass="col-md-12 col-xl-7"
        >
        <ExceptionsChart
          data={slicedExceptionsData}
          timeFrame={filtersContext.exceptionsTimeFrame}
          barHoverBackground={ colorsContext.barHoverBackground }
          exceptionsArray={colorsContext.exceptionsArray}
          isExport={false}
        />
        </DashboardTile>
    )
};

export default ExceptionsTile;